import React, {useCallback, useMemo} from 'react';

import {List, ListValue} from '../list';
import {ListWiringContext} from '../list/list-context';
import {cls} from '../../utils';
import {useDropDownContext} from './drop-down-context';

import css from './drop-down.module.css';

export type ContentProps = React.HTMLAttributes<HTMLDivElement> & {
	children?: React.ReactNode;
	/** прижать выпадашку к правому краю триггера */
	align?: 'start' | 'end';
	/** ширина: по содержимому или во всю ширину триггера */
	width?: 'auto' | 'trigger';
};

export const Content: React.FC<ContentProps> = ({children, align = 'start', width = 'auto', className, ...props}) => {
	const drop = useDropDownContext();

	const handleSelect = useCallback(
		(values: ListValue[], event: React.SyntheticEvent) => drop?.select(values, event),
		[drop],
	);

	const handleActiveChange = useCallback(
		(id: string | undefined) => drop?.setActiveDescendant(id),
		[drop],
	);

	// список внутри своего компонента (подсказки редактора) получает то же управление, что и прямой потомок
	const wiring = useMemo(
		() =>
			drop
				? {
						navigationMode: 'items' as const,
						virtualFocus: drop.virtualFocus,
						controllerRef: drop.listController,
						onActiveChange: handleActiveChange,
				  }
				: null,
		[drop, handleActiveChange],
	);

	if (!drop?.open) return null;

	// список внутри выпадашки получает выделение от неё, чтобы состояние жило в одном месте
	const content = React.Children.map(children, (list) =>
		React.isValidElement<React.ComponentProps<typeof List>>(list) && list.type === List
			? React.cloneElement(list, {
					selectMode: list.props.selectMode ?? drop.selectMode,
					selectedItems: list.props.selectedItems ?? drop.selected,
					onSelect: list.props.onSelect ?? handleSelect,
					navigationMode: list.props.navigationMode ?? 'items',
					virtualFocus: list.props.virtualFocus ?? drop.virtualFocus,
					controllerRef: list.props.controllerRef ?? drop.listController,
					onActiveChange: list.props.onActiveChange ?? handleActiveChange,
			  })
			: list,
	);

	return (
		<div
			{...props}
			id={drop.dropdownId}
			ref={drop.dropdownRef}
			className={cls(css.dropdown, align === 'end' && css.alignEnd, width === 'trigger' && css.triggerWidth, className)}
		>
			<ListWiringContext.Provider value={wiring}>{content}</ListWiringContext.Provider>
		</div>
	);
};
